import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Alert,
    Paper
} from '@mui/material';

const Profile = () => {
    const [userData, setUserData] = useState({
        username: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const fetchUserData = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/user/profile`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setUserData(prev => ({
                ...prev,
                username: response.data.username,
                email: response.data.email
            }));
        } catch (error) {
            console.error('Erreur lors de la récupération des données utilisateur:', error);
            if (error.response?.status === 401) {
                navigate('/login');
            }
        }
    }, [navigate]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    const handleChange = (e) => {
        setUserData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (userData.newPassword && userData.newPassword !== userData.confirmPassword) {
            setError('Les mots de passe ne correspondent pas');
            return;
        }

        try {
            await axios.put(
                `${API_BASE_URL}/api/user/profile`,
                {
                    username: userData.username,
                    email: userData.email,
                    currentPassword: userData.currentPassword,
                    newPassword: userData.newPassword
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                }
            );
            setSuccess('Profil mis à jour avec succès');
            setUserData(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            }));
        } catch (error) {
            setError(error.response?.data?.error || 'Une erreur est survenue');
        }
    };

    return (
        <Container component="main" maxWidth="sm">
            <Paper 
                elevation={3}
                sx={{
                    mt: 8,
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: '#1a1a1a',
                    border: '1px solid #2a2a2a'
                }}
            >
                <Typography component="h1" variant="h5" sx={{ color: '#ffffff', mb: 3 }}>
                    Modifier mon profil
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="username"
                        label="Nom d'utilisateur"
                        value={userData.username}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': {
                                    borderColor: '#2a2a2a',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#3a86ff',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3a86ff',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#888888',
                            },
                        }}
                    />

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="email"
                        label="Adresse email"
                        value={userData.email}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': {
                                    borderColor: '#2a2a2a',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#3a86ff',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3a86ff',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#888888',
                            },
                        }}
                    />

                    <TextField
                        margin="normal"
                        fullWidth
                        name="currentPassword"
                        label="Mot de passe actuel"
                        type="password"
                        value={userData.currentPassword}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': {
                                    borderColor: '#2a2a2a',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#3a86ff',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3a86ff',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#888888',
                            },
                        }}
                    />

                    <TextField
                        margin="normal"
                        fullWidth
                        name="newPassword"
                        label="Nouveau mot de passe"
                        type="password"
                        value={userData.newPassword}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': {
                                    borderColor: '#2a2a2a',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#3a86ff',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3a86ff',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#888888',
                            },
                        }}
                    />

                    <TextField
                        margin="normal"
                        fullWidth
                        name="confirmPassword"
                        label="Confirmer le nouveau mot de passe"
                        type="password"
                        value={userData.confirmPassword}
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': {
                                    borderColor: '#2a2a2a',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#3a86ff',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3a86ff',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#888888',
                            },
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{
                            mt: 3,
                            mb: 2,
                            bgcolor: '#3a86ff',
                            '&:hover': {
                                bgcolor: '#2b76ef',
                            },
                        }}
                    >
                        Enregistrer les modifications
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate('/chat')}
                        sx={{
                            color: '#3a86ff',
                            borderColor: '#3a86ff',
                            '&:hover': {
                                borderColor: '#2b76ef',
                                bgcolor: 'rgba(58, 134, 255, 0.1)',
                            },
                        }}
                    >
                        Retour au chat
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default Profile; 